import * as React from 'react';
import cn from 'classnames';
import Header from './components/Header/Header';
import FormFields from './components/FormFields/FormFields';
import { useTheme } from '../../theme';
import { getPassengerDataLabel, requiredFillData } from '../utils';
import { useForm, useFormState } from 'react-final-form';
import PopupWrapper from './components/PopupWrapper/PopupWrapper';
import { useMediaQuery } from 'react-responsive';
import { concatPassengerNames, convertGenderToCorrectValue, getPassengerTypeLabel, MOBILE_MIN_WIDTH } from '../../utils';
import { fieldIsRequired, fieldsArrayToMap } from '../../Passenger/utils';
import WindowScope from './components/WindowScope/WindowScope';
import { Collapse } from '@mui/material';
import { DocTypes, Gender, InfoMessagePositionsEnum, TravellerFieldEnum } from '@websky/graphql';
import { AdditionalTravellerFieldEnum } from '../../Passenger/types';
import { useRenderers } from '../../renderProps';
import { TravellerFields } from '../../TravellerForm/types';
import InfoMessages from '../../InfoMessages';
import { filterInfoMessagesByCode } from '../../Checkout/utils';
import { CdnIcon } from '@shared/ui';
const PassengerForm = props => {
    const theme = useTheme('PassengerForm').PassengerForm;
    const { change } = useForm();
    const { values, submitFailed } = useFormState();
    const label = React.useMemo(() => {
        const passengerValues = values.passengers[props.id];
        const firstName = passengerValues === null || passengerValues === void 0 ? void 0 : passengerValues[TravellerFields.FirstName];
        const lastName = passengerValues === null || passengerValues === void 0 ? void 0 : passengerValues[TravellerFields.LastName];
        const middleName = passengerValues === null || passengerValues === void 0 ? void 0 : passengerValues[TravellerFields.MiddleName];
        if (firstName && lastName) {
            return React.createElement("span", { className: theme.label }, concatPassengerNames({ lastName, firstName, middleName }));
        }
        return getPassengerTypeLabel(props.passengerType, props.supplierType);
    }, [props.fields, values]);
    const docType = React.useMemo(() => { var _a; return (_a = values.passengers[props.id]) === null || _a === void 0 ? void 0 : _a.docType; }, [values, props.id]);
    const isFieldRequired = React.useMemo(() => { var _a; return requiredFillData((_a = values === null || values === void 0 ? void 0 : values.passengers) === null || _a === void 0 ? void 0 : _a[props.id], values === null || values === void 0 ? void 0 : values.customer, props.fields, props.accompanyingFields); }, [values, props.fields]);
    const fieldsMap = React.useMemo(() => {
        return fieldsArrayToMap(props.fields);
    }, [props.fields]);
    const { PassengerFormFields: PassengerFormFieldsOverride = FormFields } = useRenderers();
    const passengerFieldsMap = React.useMemo(() => {
        var _a;
        if (!isFieldRequired) {
            return getPassengerDataLabel((_a = values.passengers) === null || _a === void 0 ? void 0 : _a[props.id], fieldsMap);
        }
        return {};
    }, [isFieldRequired, values, fieldsMap]);
    React.useEffect(() => {
        if (!isFieldRequired) {
            props.onCorrectFilled(props.id);
        }
    }, [isFieldRequired]);
    const isMobile = useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });
    const [hasErrors, setHasErrors] = React.useState(false);
    const isOpened = isMobile && props.mobileOpenedPassengerId === props.id;
    const isReadonly = (props.isInputsReadonly || props.isReadonly) && !props.isChangePassengers;
    const numberIcon = React.useMemo(() => {
        var _a;
        const gender = props.isChangePassengers &&
            ((_a = props.fields.find(field => field.type === TravellerFieldEnum.Gender)) === null || _a === void 0 ? void 0 : _a.defaultValue);
        if (!gender) {
            return null;
        }
        return React.createElement(CdnIcon, { icon: convertGenderToCorrectValue(gender) === Gender.Male ? 'Info/Male' : 'Info/Female' });
    }, [props.fields, props.isChangePassengers]);
    const fields = React.useMemo(() => {
        if (props.isChangePassengers) {
            const isPassport = docType === DocTypes.Passport;
            return props.fields.filter(field => {
                if ([
                    TravellerFieldEnum.LinkedTraveller,
                    TravellerFieldEnum.Snils,
                    AdditionalTravellerFieldEnum.Subsidy
                ].includes(field.type)) {
                    return false;
                }
                return !isPassport || (isPassport && field.type !== TravellerFieldEnum.DocExpiryDate);
            });
        }
        return props.fields;
    }, [docType, props.fields, props.isChangePassengers]);
    React.useEffect(() => {
        if (isMobile && submitFailed && isFieldRequired && props.open) {
            setHasErrors(true);
            if (props.mobileOpenedPassengerId === null) {
                props.onSetMobileOpenedPassengerId(props.id);
            }
        }
        else if (!isFieldRequired) {
            setHasErrors(false);
        }
    }, [submitFailed, isMobile, isFieldRequired, props.open]);
    const onCloseHandler = () => props.onSetMobileOpenedPassengerId(null);
    const onOpenHandler = () => props.onSetMobileOpenedPassengerId(props.id);
    const onFillHandler = (profile, type, fieldValue) => {
        var _a, _b, _c, _d, _e, _f;
        if (type) {
            const docExpiryValidations = ((_a = props.fields.find(field => field.type === TravellerFieldEnum.DocExpiryDate)) === null || _a === void 0 ? void 0 : _a.validations) || [];
            if ((type === TravellerFieldEnum.Nationality || type === TravellerFieldEnum.DocType) &&
                (((_c = (_b = values.passengers) === null || _b === void 0 ? void 0 : _b[props.id]) === null || _c === void 0 ? void 0 : _c.docExpiryDate) || ((_e = (_d = values.passengers) === null || _d === void 0 ? void 0 : _d[props.id]) === null || _e === void 0 ? void 0 : _e.nationality)) &&
                fieldIsRequired(values.passengers[props.id], docExpiryValidations)) {
                change(`passengers[${props.id}].docExpiryDate`, '');
            }
        }
        (_f = props.onFill) === null || _f === void 0 ? void 0 : _f.call(props, profile, fieldValue);
    };
    const filteredInfoMessages = React.useMemo(() => {
        var _a;
        const passenger = (_a = values.passengers) === null || _a === void 0 ? void 0 : _a[props.id];
        return passenger && props.infoMessages ? filterInfoMessagesByCode(props.infoMessages, [passenger]) : [];
    }, [props.infoMessages, values.passengers, props.id]);
    const showInfoMessages = filteredInfoMessages.length > 0;
    return (React.createElement("div", { className: cn(theme.form, props.className), "data-passenger-type": props.passengerType, "data-passenger-id": props.id },
        React.createElement(WindowScope, { fieldsMap: fieldsMap, disabled: isReadonly },
            React.createElement(Header, { open: props.open, passengerId: props.id, label: label, numberIcon: numberIcon, onClick: onOpenHandler, correct: !isFieldRequired, passengerFieldsMap: passengerFieldsMap, showErrorNotice: hasErrors, hideClearButton: props.hideClearButton }),
            React.createElement(Collapse, { in: props.open, unmountOnExit: true, mountOnEnter: true },
                React.createElement(PopupWrapper, { inline: !isMobile, open: isOpened, onClose: onCloseHandler, correct: !isFieldRequired },
                    showInfoMessages && (React.createElement(InfoMessages, { className: theme.infoMessages, messages: filteredInfoMessages, position: [InfoMessagePositionsEnum.passengerCard] })),
                    React.createElement(PassengerFormFieldsOverride, { fields: fields, passengerId: props.id, passengerType: props.passengerType, passengerDocType: docType, selectedProfileId: props.selectedProfileId, label: label, inline: !isMobile, disabled: isReadonly, onTravellerSelect: onFillHandler, isAuthorized: props.isAuthorized, isChangeFiles: props.isChangeFiles, isChangePassengers: props.isChangePassengers, autofocus: props.autofocus, accompanyingFields: props.accompanyingFields, onSignUpLoyaltyProgram: props.onSignUpLoyaltyProgram, onSwap: props.onSwap, onToggleChangeFiles: props.onToggleChangeFiles }))))));
};
export default PassengerForm;
